import { useCallback, useEffect, type PropsWithChildren } from 'react';

import { Spin } from 'antd';

import { useGetCasdoorAuthDataMutation } from '@/api/endpoints/casdoorAuth';
import { useGetTeachbaseAuthDataMutation } from '@/api/endpoints/teachbase';
import { ENVS } from '@/shared/consts';
import {
  getCasdoorAccessToken,
  getPublicSiteUrl,
  getSSOUrlToRedirect,
  getLMSAccessToken,
  saveCasdoorTokens,
  saveLMSTokens,
  removeOldLMSTokens,
} from '@/shared/helpers';

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [fetchCasdoorAuthData] = useGetCasdoorAuthDataMutation();
  const [fetchTeachbaseAuthData] = useGetTeachbaseAuthDataMutation();

  const casdoorAccessToken = getCasdoorAccessToken();
  const lmsAccessToken = getLMSAccessToken();

  const searchParams = new URLSearchParams(window.location.search);

  const authorizationCode = searchParams.get('authorization_code') || '';
  const codeVerifier = searchParams.get('code_verifier') || '';

  const shouldNavigateToSSO = Boolean(!casdoorAccessToken && (!codeVerifier || !authorizationCode));
  const shouldFetchTokens = Boolean(authorizationCode && codeVerifier);

  const getAuthData = useCallback(async () => {
    try {
      const { access_token: casdoorAccessToken, refresh_token: casdoorRefreshToken } = await fetchCasdoorAuthData({
        authorizationCode,
        codeVerifier,
      }).unwrap();

      saveCasdoorTokens({ casdoorAccessToken, casdoorRefreshToken });

      const { access_token: lmsAccessToken, refresh_token: lmsRefreshToken } = await fetchTeachbaseAuthData().unwrap();

      saveLMSTokens({ lmsAccessToken, lmsRefreshToken });

      window.location.replace(`${window.location.origin}${window.location.pathname}`);
    } catch {
      window.location.href = getPublicSiteUrl();
    }
  }, [fetchCasdoorAuthData, fetchTeachbaseAuthData, authorizationCode, codeVerifier]);

  useEffect(() => {
    if (shouldNavigateToSSO && ENVS.MODE === 'mock') {
      saveCasdoorTokens({ casdoorAccessToken: '123', casdoorRefreshToken: '321' });
      saveLMSTokens({ lmsAccessToken: '123', lmsRefreshToken: '321' });

      return;
    }

    if (shouldNavigateToSSO) {
      window.location.href = getSSOUrlToRedirect({
        type: 'admin',
        redirectUri: `${window.location.origin}${window.location.pathname}`,
      });
    } else if (shouldFetchTokens) {
      getAuthData();
    }
  }, [getAuthData, shouldNavigateToSSO, shouldFetchTokens]);

  // TODO Remove after DEV-33177 release
  useEffect(() => {
    removeOldLMSTokens();
  }, [removeOldLMSTokens]);

  if (casdoorAccessToken && lmsAccessToken && (!authorizationCode || !codeVerifier)) {
    return children;
  }

  return (
    <Spin
      fullscreen
      size="large"
    />
  );
};
